import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";

const gbp = (n) => `£${n.toFixed(2)}`;

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();

  const quickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!addItem(product, "product", 1)) return;
    toast.success(`${product.name} added to cart`, { description: "Lawful research & educational use only." });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.07 }}
    >
      <Link
        to={`/product/${product.id}`}
        data-testid="product-card"
        className="group relative flex h-full flex-col overflow-hidden rounded-2xl glass metallic-border card-hover"
      >
        <div className="relative aspect-square overflow-hidden bg-slate-100">
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className={`h-full w-full object-cover transition-transform duration-700 group-hover:scale-105 ${product.sold_out ? "opacity-50 grayscale" : ""}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-transparent to-transparent opacity-80" />
          <div className="absolute left-3 top-3 flex flex-col gap-1.5">
            {product.compare_at && product.compare_at > product.price && (
              <span className="rounded-full bg-white/90 px-2.5 py-0.5 text-[10px] font-mono2 font-semibold uppercase tracking-wider text-rose-600 ring-1 ring-rose-200">Sale</span>
            )}
            {product.new_arrival && (
              <span className="rounded-full bg-white/90 px-2.5 py-0.5 text-[10px] font-mono2 font-semibold uppercase tracking-wider text-sky-600 ring-1 ring-sky-200">New</span>
            )}
            {product.purity && product.purity !== "N/A" && (
              <span className="rounded-full bg-white/90 px-2.5 py-0.5 text-[10px] font-mono2 font-semibold uppercase tracking-wider text-emerald-600 ring-1 ring-emerald-200">{product.purity}</span>
            )}
          </div>
          {product.sold_out && (
            <span data-testid="product-sold-out-badge" className="absolute inset-x-0 top-1/2 mx-auto w-fit -translate-y-1/2 rounded-full bg-slate-900/90 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-white">Sold out</span>
          )}
          {!product.sold_out && <button
            onClick={quickAdd}
            data-testid="product-add-to-cart-btn"
            aria-label="Add to cart"
            className="absolute bottom-3 right-3 grid h-10 w-10 translate-y-2 place-items-center rounded-full btn-metallic opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100"
          >
            <Plus className="h-5 w-5" />
          </button>}
        </div>

        <div className="flex flex-1 flex-col p-5">
          <span className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">{product.category}</span>
          <h3 className="mt-1.5 font-display text-lg font-semibold leading-tight text-slate-900">{product.name}</h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-slate-600">{product.overview}</p>
          <div className="mt-4 flex items-center justify-between border-t border-slate-200/80 pt-4">
            <span className="flex items-baseline gap-2">
              <span className="text-lg font-semibold metallic-text">{gbp(product.price)}</span>
              {product.compare_at && product.compare_at > product.price && (
                <span className="text-sm text-slate-400 line-through">{gbp(product.compare_at)}</span>
              )}
            </span>
            <span data-testid="product-stock-status" className={`inline-flex items-center gap-1 text-xs ${product.sold_out ? "text-rose-500" : "text-emerald-500"}`}>
              {!product.sold_out && <Check className="h-3.5 w-3.5" />} {product.status}
            </span>
          </div>
          <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 group-hover:text-slate-900">
            View Details <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

export const ProductGrid = ({ products }) => (
  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
    {products.map((p, i) => (
      <ProductCard key={p.id} product={p} index={i} />
    ))}
  </div>
);

export default ProductCard;
